"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, Clock } from "lucide-react";
import { trackBlogRead } from "@/utils/gtm";

export type BlogEntry = {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  readTime: string;
};

const FILTER_TABS = [
  "All",
  "Career Guides",
  "Course Spotlights",
  "Industry Trends",
  "Study Tips",
];

function normalizeCategory(category: string): string {
  if (category.startsWith("Study Tips")) return "Study Tips";
  return category;
}

const CATEGORY_STYLES: Record<string, { bar: string; badge: string }> = {
  "Career Guides": {
    bar: "bg-blue-50 border-b border-blue-100",
    badge: "text-blue-700 bg-blue-50",
  },
  "Course Spotlights": {
    bar: "bg-violet-50 border-b border-violet-100",
    badge: "text-violet-700 bg-violet-50",
  },
  "Industry Trends": {
    bar: "bg-emerald-50 border-b border-emerald-100",
    badge: "text-emerald-700 bg-emerald-50",
  },
  "Study Tips": {
    bar: "bg-amber-50 border-b border-amber-100",
    badge: "text-amber-700 bg-amber-50",
  },
};

const DEFAULT_STYLE = {
  bar: "bg-gray-50 border-b border-gray-100",
  badge: "text-gray-600 bg-gray-50",
};

export default function BlogListingClient({
  entries,
  showFilters = true,
  initialTab = "All",
}: {
  entries: BlogEntry[];
  showFilters?: boolean;
  initialTab?: string;
}) {
  const [activeTab, setActiveTab] = useState(initialTab);

  const availableTabs = useMemo(() => {
    if (!showFilters) {
      return [initialTab];
    }

    return FILTER_TABS;
  }, [initialTab, showFilters]);

  const filtered =
    activeTab === "All"
      ? entries
      : entries.filter((e) => normalizeCategory(e.category) === activeTab);

  const tabCounts = availableTabs.reduce<Record<string, number>>((acc, tab) => {
    acc[tab] =
      tab === "All"
        ? entries.length
        : entries.filter((e) => normalizeCategory(e.category) === tab).length;
    return acc;
  }, {});

  return (
    <div>
      {showFilters ? (
        <div className="flex flex-wrap gap-2 md:gap-3 mb-10">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${
                activeTab === tab
                  ? "bg-[#1B61FF] text-white shadow-md shadow-blue-100"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab}
              <span
                className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${
                  activeTab === tab
                    ? "bg-white/20 text-white"
                    : "bg-white text-gray-500"
                }`}
              >
                {tabCounts[tab]}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {/* Post grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
        {filtered.map((post) => {
          const cat = normalizeCategory(post.category);
          const style = CATEGORY_STYLES[cat] ?? DEFAULT_STYLE;

          return (
            <article
              key={post.slug}
              className="bg-white rounded-2xl border border-gray-200 hover:shadow-xl transition-all duration-300 group flex flex-col overflow-hidden"
            >
              {/* Category bar */}
              <div className={`px-4 py-2.5 ${style.bar}`}>
                <span className={`text-[11px] font-extrabold uppercase tracking-wider ${style.badge}`}>
                  {post.category}
                </span>
              </div>

              <div className="p-5 flex flex-col flex-1">
                <h2 className="text-[#002147] text-[20px] font-bold leading-snug mb-3 group-hover:text-[#1B61FF] transition-colors line-clamp-3">
                  <Link href={`/blog/${post.slug}`}>{post.title}</Link>
                </h2>

                <p className="text-gray-500 text-[18px] leading-relaxed line-clamp-4 flex-1 mb-5">
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                    <Clock size={12} />
                    {post.readTime}
                  </span>
                  <Link
                    href={`/blog/${post.slug}`}
                    onClick={() => trackBlogRead(post.title, post.category)}
                    className="inline-flex items-center gap-1 text-sm font-bold text-[#1B61FF] hover:gap-2 transition-all"
                  >
                    Read More <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-gray-500 py-20 text-base">
          No posts in this category yet.
        </p>
      )}
    </div>
  );
}
